import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/config";
import PrivateTripCard from "../components/PrivateTripCard";
import { Reveal, SplitLines } from "../components/Motion";
import Seo from "../components/Seo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Search } from "lucide-react";

export default function PrivateTrips() {
  const [trips, setTrips] = useState([]);
  const [destination, setDestination] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/trips", { params: { trip_type: "Private" } }).then((r) => setTrips(r.data));
  }, []);

  const destinations = useMemo(() => {
    const set = new Set(trips.map((t) => t.country));
    return ["all", ...Array.from(set)];
  }, [trips]);

  const filtered = useMemo(
    () => trips.filter((t) => {
      if (destination !== "all" && t.country !== destination) return false;
      if (q && !`${t.name} ${t.destination} ${t.country}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    }),
    [trips, destination, q]
  );

  return (
    <div className="pt-28 md:pt-36 pb-16" data-testid="private-trips-page">
      <Seo
        title="Private Trips"
        description="Handcrafted private journeys — designed around you. Currently featuring Kenya. WhatsApp us and we'll design it around your pace, group and dates."
        path="/private-trips"
      />

      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <Reveal>
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-muted mb-4">Private Trips</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9] text-owt-forest">
            <SplitLines text="Just you,"/><br/>
            <span className="italic text-owt-terracotta"><SplitLines text="just your way." delay={0.15}/></span>
          </h1>
          <p className="mt-6 max-w-2xl text-owt-muted leading-relaxed">
            Private, hand-designed journeys for couples, families and small groups. Same care as our group trips — with the pace, stays and dates you choose.
          </p>
        </Reveal>

        {/* Search + destination dropdown */}
        <div className="mt-10 md:mt-14 border-y border-owt-border py-6 flex flex-col md:flex-row md:items-center gap-6" data-testid="private-filters">
          <div className="flex items-center gap-3 flex-1">
            <Search className="w-4 h-4 text-owt-muted"/>
            <input
              value={q}
              onChange={(e)=>setQ(e.target.value)}
              placeholder="Search a destination... Kenya, Kyrgyzstan, Japan..."
              className="bg-transparent border-b border-owt-border pb-2 outline-none font-mono text-sm text-owt-forest placeholder:text-owt-muted flex-1"
              data-testid="private-search"
            />
          </div>
          <div className="md:w-64">
            <Select value={destination} onValueChange={setDestination}>
              <SelectTrigger data-testid="private-destination-select" className="font-mono text-[11px] tracking-[0.15em] uppercase">
                <SelectValue placeholder="Choose destination"/>
              </SelectTrigger>
              <SelectContent>
                {destinations.map((d) => (
                  <SelectItem key={d} value={d}>{d === "all" ? "All destinations" : d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mt-12">
          {filtered.map((t, i) => <PrivateTripCard key={t.id} trip={t} index={i}/>)}
          {filtered.length === 0 && (
            <div className="col-span-full py-16 text-center text-owt-muted font-mono text-sm tracking-[0.15em] uppercase">
              No matching private trip yet — WhatsApp us and we'll craft one.
            </div>
          )}
        </div>

        {/* Coming-soon teaser */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-24 border border-dashed border-owt-border p-8 md:p-12 text-center bg-owt-sand/30"
        >
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-owt-terracotta mb-3">Coming soon</div>
          <h3 className="font-display text-3xl md:text-5xl text-owt-forest leading-tight">
            Kyrgyzstan, Japan, Vietnam & more <span className="italic text-owt-terracotta">— private editions.</span>
          </h3>
          <p className="mt-4 text-owt-muted max-w-2xl mx-auto">Have somewhere else in mind? Tell us — we've been to seventeen countries and we'll design your route from scratch.</p>
        </motion.div>
      </div>
    </div>
  );
}
